import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  HotelsAvailabilitySelectors,
  HotelsShopSelectors,
} from "@b2bportal/core-hotels";
import type { DateRange } from "@b2bportal/lodging-api";
import { DateTimeFormatStyle, useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";

interface UseHotelDateRangeReturn {
  totalNights?: number;
  dateRange: DateRange;
  dateFormattedString: string;
}

interface UseHotelDateRangeProps {
  useShopData?: boolean;
  separator?: string;
  dateFormat?: DateTimeFormatStyle;
  fromDateFormat?: DateTimeFormatStyle;
  untilDateFormat?: DateTimeFormatStyle;
}

export const useHotelDateRange = ({
  useShopData = false,
  separator = " ~ ",
  dateFormat = DateTimeFormatStyle.ShortMonthDayShortWeekday,
  fromDateFormat,
  untilDateFormat,
}: UseHotelDateRangeProps = {}): UseHotelDateRangeReturn => {
  const { formatDateTime } = useI18nContext();

  const shopFrom = useSelector(HotelsShopSelectors.getFromDate);
  const shopUntil = useSelector(HotelsShopSelectors.getUntilDate);

  let { from, until } =
    useSelector(HotelsAvailabilitySelectors.getSearchFormDateRange) || {};

  // users visiting hotel shop directly won't have search form date range
  if (useShopData) {
    from = shopFrom;
    until = shopUntil;
  }

  const formatDate = useCallback(
    (date: string, format: DateTimeFormatStyle) =>
      formatDateTime(dayjs(date).toDate(), format),
    [formatDateTime]
  );

  const totalNights = useMemo(() => {
    if (from != null && until != null) {
      const fromDate = dayjs(from);
      const untilDate = dayjs(until);

      return untilDate.diff(fromDate, "days");
    }

    return undefined;
  }, [from, until]);

  const dateRange = useMemo(
    () => ({
      from: from ? formatDate(from, fromDateFormat ?? dateFormat) : "",
      until: until ? formatDate(until, untilDateFormat ?? dateFormat) : "",
    }),
    [from, until, formatDate]
  );

  return {
    totalNights,
    dateRange,
    dateFormattedString: `${dateRange.from}${separator}${dateRange.until}`,
  };
};
